/**
 * Routes Page
 * View dispatched routes for the day
 * Supports manual driver assignment for unassigned routes
 */

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Input, CoolingBadge } from '../components/ui';
import {
  RouteCard,
  RouteMap,
  RouteDetailPanel,
} from '../components/routes';
import {
  useRoutesByDate,
} from '../hooks/useApi';
import { usePolling } from '../hooks/usePolling';
import { Calendar, Loader2, Truck, MapPin, CheckCircle, AlertCircle, X, Search, Plus, RefreshCw } from 'lucide-react';
import { getAvailableDrivers } from '../services/drivers.api';
import { assignDriverToRouteWithProgress, unassignDriverFromRouteWithProgress, deleteRoute, getRoute, removeStopFromRoute, addStopToRoute, retryTaskAssignment, retryTaskUnassignment, syncRouteStatus, type CoreApiStatus, type ProgressEvent, type StreamCompleteEvent, type SyncStatusResult } from '../services/routes.api';
import { getTasks, markTaskAsDone } from '../services/tasks.api';
import { Button } from '../components/ui';
import type { Route, RouteStop, RoutingTask } from '../types';
import { getBaghdadOperationalDate } from '../utils/pickupSlotStatus';
import { matchesSearch } from '../utils/search';
import { useCan } from '../contexts/useAuth';

interface Driver {
  driver_uid: string;
  full_name: string;
}

interface LocationState {
  routeRunId?: string;
  routeDate?: string;
  message?: string;
}

interface RouteOperationState {
  isLoading: boolean;
  operation: 'assign' | 'unassign' | 'change' | null;
  progress: { current: number; total: number } | null;
  coreApiStatus: CoreApiStatus | null;
  driverUid?: string;
}

export default function Routes() {
  const can = useCan();
  const location = useLocation();
  const locationState = location.state as LocationState | null;

  // State
  const [selectedRouteId, setSelectedRouteId] = useState<string | null>(null);
  const [selectedStopId, setSelectedStopId] = useState<string | null>(null);
  // Routes live on the operational day (04:00→04:00 Baghdad): a dispatcher
  // checking an active night shift after midnight must still land on it.
  const [date, setDate] = useState(locationState?.routeDate || getBaghdadOperationalDate());
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(locationState?.message || null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [stopsMap, setStopsMap] = useState<Map<string, RouteStop[]>>(new Map());
  const [isLoadingStops, setIsLoadingStops] = useState(false);
  const [isDeletingRoute, setIsDeletingRoute] = useState<string | null>(null);
  const [routeOperations, setRouteOperations] = useState<Map<string, RouteOperationState>>(new Map());
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [mapCenterLocation, setMapCenterLocation] = useState<{ lat: number; lng: number } | undefined>(undefined);
  const [showAddStopModal, setShowAddStopModal] = useState(false);
  const [unassignedTasks, setUnassignedTasks] = useState<RoutingTask[]>([]);
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  const [addStopSearch, setAddStopSearch] = useState('');
  const [isAddingStop, setIsAddingStop] = useState(false);
  const [addStopStartDate, setAddStopStartDate] = useState(date);
  const [addStopEndDate, setAddStopEndDate] = useState(date);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [routeStatusMap, setRouteStatusMap] = useState<Map<string, SyncStatusResult>>(new Map());
  const [isPolling, setIsPolling] = useState(false);
  const [lastPolledAt, setLastPolledAt] = useState<Date | null>(null);
  const cityId = 1; // Baghdad

  // Queries
  const { data: routes, isLoading: isLoadingRoutes, refetch: refetchRoutes, isFetching: isFetchingRoutes } = useRoutesByDate(cityId, date);

  // Fetch available drivers for assignment (filtered by schedule for the selected date)
  useEffect(() => {
    async function fetchDrivers() {
      try {
        const data = await getAvailableDrivers(cityId, date);
        // Map to the Driver interface expected by RouteCard
        setDrivers(data.map(d => ({
          driver_uid: d.driverUid,
          full_name: d.driverName,
        })));
      } catch (error) {
        console.error('Failed to fetch drivers:', error);
      }
    }
    fetchDrivers();
  }, [date, cityId]);

  // Check if any route operation is in progress
  const hasOngoingOperations = useMemo(() => {
    for (const op of routeOperations.values()) {
      if (op.isLoading) return true;
    }
    return isAddingStop || isDeletingRoute !== null;
  }, [routeOperations, isAddingStop, isDeletingRoute]);

  // Polling function for auto-refresh
  const pollingFn = useCallback(async () => {
    const { getRoutesByDate } = await import('../services/routes.api');
    return getRoutesByDate(cityId, date);
  }, [cityId, date]);

  // Handle data change from polling
  const handlePollingDataChange = useCallback(async (newRoutes: Route[]) => {
    setIsPolling(true);
    setLastPolledAt(new Date());
    
    try {
      // Refetch routes from react-query
      await refetchRoutes();
      
      // Also sync status for all routes to get updated task statuses
      if (newRoutes && newRoutes.length > 0) {
        const statusResults = await Promise.all(
          newRoutes.map(route => syncRouteStatus(route._id))
        );
        const newStatusMap = new Map<string, SyncStatusResult>();
        statusResults.forEach((result, index) => {
          newStatusMap.set(newRoutes[index]._id, result);
        });
        setRouteStatusMap(newStatusMap);
      }
    } catch (error) {
      console.error('Failed to sync route statuses during polling:', error);
    } finally {
      setTimeout(() => setIsPolling(false), 100);
    }
  }, [refetchRoutes]);

  // Setup polling - 30 second interval with exponential backoff on errors
  // Disable polling during ongoing operations to avoid conflicts
  usePolling(pollingFn, 30000, {
    enabled: !isLoadingRoutes && !isFetchingRoutes && !hasOngoingOperations && !isRefreshing,
    immediate: false,
    maxInterval: 120000,
    onDataChange: handlePollingDataChange,
    onError: (error) => {
      console.error('Routes polling error:', error);
    },
  });

  // Prevent page reload/close during ongoing Core API operations
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasOngoingOperations) {
        e.preventDefault();
        e.returnValue = 'Changes are being synced with the server. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasOngoingOperations]);

  // Helper to update route operation state
  const updateRouteOperation = (routeId: string, update: Partial<RouteOperationState> | null) => {
    setRouteOperations(prev => {
      const newMap = new Map(prev);
      if (update === null) {
        newMap.delete(routeId);
      } else {
        const current = newMap.get(routeId) || { isLoading: false, operation: null, progress: null, coreApiStatus: null };
        newMap.set(routeId, { ...current, ...update });
      }
      return newMap;
    });
  };

  // Handle driver assignment with streaming progress
  const handleAssignDriver = (routeId: string, driverUid: string) => {
    // Set loading state
    updateRouteOperation(routeId, {
      isLoading: true,
      operation: 'assign',
      progress: null,
      coreApiStatus: null,
      driverUid
    });

    assignDriverToRouteWithProgress(routeId, driverUid, {
      onStart: (data) => {
        updateRouteOperation(routeId, {
          isLoading: true,
          operation: data.operation === 'change' ? 'change' : 'assign',
          progress: { current: 0, total: data.totalTasks },
          driverUid,
        });
      },
      onProgress: (data: ProgressEvent) => {
        updateRouteOperation(routeId, {
          progress: { current: data.current, total: data.total },
        });
      },
      onComplete: async (data: StreamCompleteEvent) => {
        updateRouteOperation(routeId, {
          isLoading: false,
          progress: null,
          coreApiStatus: data.coreApiStatus
        });

        if (data.coreApiStatus?.success) {
          setSuccessMessage(data.message);
          setTimeout(() => {
            setSuccessMessage(null);
            updateRouteOperation(routeId, null);
          }, 3000);
          await refetchRoutes();
        } else if (data.coreApiStatus && data.coreApiStatus.failedTasks > 0) {
          setErrorMessage(`${data.coreApiStatus.failedTasks} task(s) failed to assign. You can retry them.`);
          setTimeout(() => setErrorMessage(null), 5000);
          // Don't refetch if there are failures - keep the operation state for retry
        } else {
          // Complete failure - clear operation state
          await refetchRoutes();
        }
      },
      onError: (error) => {
        console.error('Failed to assign driver:', error);
        updateRouteOperation(routeId, { isLoading: false, progress: null });
        setErrorMessage(error);
        setTimeout(() => setErrorMessage(null), 5000);
      },
    });
  };

  // Handle driver removal with streaming progress
  const handleRemoveDriver = (routeId: string) => {
    // Get current driver UID before removal
    const route = routes?.find(r => r._id === routeId);
    const driverUid = route?.driver_uid;

    // Set loading state
    updateRouteOperation(routeId, {
      isLoading: true,
      operation: 'unassign',
      progress: null,
      coreApiStatus: null,
      driverUid
    });

    unassignDriverFromRouteWithProgress(routeId, {
      onStart: (data) => {
        updateRouteOperation(routeId, {
          isLoading: true,
          operation: 'unassign',
          progress: { current: 0, total: data.totalTasks },
          driverUid,
        });
      },
      onProgress: (data: ProgressEvent) => {
        updateRouteOperation(routeId, {
          progress: { current: data.current, total: data.total },
        });
      },
      onComplete: async (data: StreamCompleteEvent) => {
        updateRouteOperation(routeId, {
          isLoading: false,
          progress: null,
          coreApiStatus: data.coreApiStatus
        });

        if (data.coreApiStatus?.success) {
          setSuccessMessage(data.message);
          setTimeout(() => {
            setSuccessMessage(null);
            updateRouteOperation(routeId, null);
          }, 3000);
          await refetchRoutes();
        } else if (data.coreApiStatus && data.coreApiStatus.failedTasks > 0) {
          setErrorMessage(`${data.coreApiStatus.failedTasks} task(s) failed to unassign. You can retry them.`);
          setTimeout(() => setErrorMessage(null), 5000);
          // Don't refetch if there are failures - keep the operation state for retry
        } else {
          // Complete failure - clear operation state
          await refetchRoutes();
        }
      },
      onError: (error) => {
        console.error('Failed to remove driver:', error);
        updateRouteOperation(routeId, { isLoading: false, progress: null });
        setErrorMessage(error);
        setTimeout(() => setErrorMessage(null), 5000);
      },
    });
  };

  // Handle retry for failed task assignments
  const handleRetryTask = async (routeId: string, taskUid: string, operation: 'assign' | 'unassign', priority?: number) => {
    const routeOp = routeOperations.get(routeId);
    if (!routeOp) return;

    try {
      let result;
      if (operation === 'assign' && routeOp.driverUid) {
        result = await retryTaskAssignment(taskUid, routeOp.driverUid, priority);
      } else {
        result = await retryTaskUnassignment(taskUid, routeOp.driverUid);
      }

      if (result.success) {
        // Remove from failed list
        const newStatus = { ...routeOp.coreApiStatus } as CoreApiStatus;
        if (operation === 'assign' && newStatus.failedAssignments) {
          newStatus.failedAssignments = newStatus.failedAssignments.filter(f => f.taskUid !== taskUid);
          newStatus.failedTasks = newStatus.failedAssignments.length;
          newStatus.assignedTasks = (newStatus.assignedTasks || 0) + 1;
        } else if (operation === 'unassign' && newStatus.failedUnassignments) {
          newStatus.failedUnassignments = newStatus.failedUnassignments.filter(f => f.taskUid !== taskUid);
          newStatus.failedTasks = newStatus.failedUnassignments.length;
          newStatus.unassignedTasks = (newStatus.unassignedTasks || 0) + 1;
        }
        newStatus.success = newStatus.failedTasks === 0;

        updateRouteOperation(routeId, { coreApiStatus: newStatus });

        if (newStatus.success) {
          setSuccessMessage('All tasks successfully processed!');
          setTimeout(() => {
            setSuccessMessage(null);
            updateRouteOperation(routeId, null);
          }, 3000);
        }
      } else {
        setErrorMessage(`Failed to retry: ${result.error}`);
        setTimeout(() => setErrorMessage(null), 5000);
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to retry task');
      setTimeout(() => setErrorMessage(null), 5000);
    }
  };

  // Handle retry all failed tasks - execute in parallel
  const handleRetryAllTasks = async (routeId: string) => {
    const routeOp = routeOperations.get(routeId);
    if (!routeOp?.coreApiStatus) return;

    const failedTasks = routeOp.operation === 'assign'
      ? routeOp.coreApiStatus.failedAssignments
      : routeOp.coreApiStatus.failedUnassignments;

    if (!failedTasks || failedTasks.length === 0) return;

    updateRouteOperation(routeId, { isLoading: true });

    try {
      // Execute all retries in parallel
      const results = await Promise.allSettled(
        failedTasks.map(task => {
          if (routeOp.operation === 'assign' && routeOp.driverUid) {
            return retryTaskAssignment(task.taskUid, routeOp.driverUid, task.priority);
          } else {
            return retryTaskUnassignment(task.taskUid, routeOp.driverUid);
          }
        })
      );

      // Process results and update status
      const newStatus = { ...routeOp.coreApiStatus } as CoreApiStatus;
      let successCount = 0;

      results.forEach((result, index) => {
        const task = failedTasks[index];
        if (result.status === 'fulfilled' && result.value.success) {
          successCount++;
          // Remove from failed list
          if (routeOp.operation === 'assign' && newStatus.failedAssignments) {
            newStatus.failedAssignments = newStatus.failedAssignments.filter(f => f.taskUid !== task.taskUid);
            newStatus.assignedTasks = (newStatus.assignedTasks || 0) + 1;
          } else if (routeOp.operation === 'unassign' && newStatus.failedUnassignments) {
            newStatus.failedUnassignments = newStatus.failedUnassignments.filter(f => f.taskUid !== task.taskUid);
            newStatus.unassignedTasks = (newStatus.unassignedTasks || 0) + 1;
          }
        }
      });

      newStatus.failedTasks = (newStatus.failedAssignments?.length || 0) + (newStatus.failedUnassignments?.length || 0);
      newStatus.success = newStatus.failedTasks === 0;

      updateRouteOperation(routeId, { coreApiStatus: newStatus });

      if (newStatus.success) {
        setSuccessMessage('All tasks successfully processed!');
        setTimeout(() => {
          setSuccessMessage(null);
          updateRouteOperation(routeId, null);
        }, 3000);
        await refetchRoutes();
      } else if (successCount > 0) {
        setSuccessMessage(`${successCount} task(s) retried successfully. ${newStatus.failedTasks} still failed.`);
        setTimeout(() => setSuccessMessage(null), 5000);
      } else {
        setErrorMessage('All retry attempts failed. Please try again.');
        setTimeout(() => setErrorMessage(null), 5000);
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to retry tasks');
      setTimeout(() => setErrorMessage(null), 5000);
    } finally {
      updateRouteOperation(routeId, { isLoading: false });
    }
  };

  // Handle route deletion
  const handleDeleteRoute = async (routeId: string) => {
    if (!confirm('Are you sure you want to delete this route?')) return;

    setIsDeletingRoute(routeId);
    setErrorMessage(null);
    try {
      await deleteRoute(routeId);
      setSuccessMessage('Route deleted successfully');
      // Refetch to update the UI
      await refetchRoutes();
      // Clear selection if deleted route was selected
      if (selectedRouteId === routeId) {
        setSelectedRouteId(null);
      }
      // Clear message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to delete route');
      setTimeout(() => setErrorMessage(null), 5000);
    } finally {
      setIsDeletingRoute(null);
    }
  };

  // Fetch stops for all routes when routes data changes
  // Use a ref to track if we've already fetched for these routes
  const fetchedRoutesRef = useRef<string>('');

  useEffect(() => {
    let isCancelled = false;

    async function fetchStops() {
      if (!routes || routes.length === 0) {
        setStopsMap(new Map());
        setIsLoadingStops(false);
        return;
      }

      // Create a key from route IDs to check if we need to refetch
      const routeKey = routes.map(r => r._id).sort().join(',');
      if (routeKey === fetchedRoutesRef.current) {
        // Already fetched for these routes, skip
        return;
      }

      setIsLoadingStops(true);
      const newStopsMap = new Map<string, RouteStop[]>();

      try {
        // Fetch stops for each route in parallel with timeout
        const stopsPromises = routes.map(async (route) => {
          try {
            // Add timeout to prevent hanging - reduced to 5 seconds
            const timeoutPromise = new Promise<never>((_, reject) => {
              setTimeout(() => reject(new Error('Request timeout')), 5000);
            });
            const fetchPromise = getRoute(route._id);
            const data = await Promise.race([fetchPromise, timeoutPromise]);
            return { routeId: route._id, stops: data.stops || [] };
          } catch (error) {
            console.error(`Failed to fetch stops for route ${route._id}:`, error);
            return { routeId: route._id, stops: [] };
          }
        });

        const results = await Promise.all(stopsPromises);

        if (!isCancelled) {
          results.forEach(({ routeId, stops }) => {
            newStopsMap.set(routeId, stops);
          });
          setStopsMap(newStopsMap);
          fetchedRoutesRef.current = routeKey;

          // Auto-fetch route statuses on initial load
          (async () => {
            try {
              const syncPromises = routes.map(route => syncRouteStatus(route._id));
              const statusResults = await Promise.all(syncPromises);

              const newStatusMap = new Map<string, SyncStatusResult>();
              statusResults.forEach((result, index) => {
                newStatusMap.set(routes[index]._id, result);
              });
              setRouteStatusMap(newStatusMap);
            } catch (error) {
              console.error('Failed to auto-fetch route statuses:', error);
            }
          })();
        }
      } catch (error) {
        console.error('Failed to fetch stops:', error);
      } finally {
        if (!isCancelled) {
          setIsLoadingStops(false);
        }
      }
    }

    fetchStops();

    return () => {
      isCancelled = true;
    };
  }, [routes]);

  // Calculate summary stats for all routes (shown at top of page)
  const summary = useMemo(() => {
    if (!routes) return { routes: 0, stops: 0, distance: 0, versoDurationMin: 0, googleDurationMin: 0, cost: 0 };
    return {
      routes: routes.length,
      // Exclude cancelled stops (matches the route cards): subtract the synced
      // cancelled count per route from its stored stop_count. Distance, duration,
      // and cost stay as the originally planned values.
      stops: routes.reduce((sum, r) => {
        const cancelled = routeStatusMap.get(r._id)?.progress.cancelled ?? 0;
        return sum + Math.max(0, r.stop_count - cancelled);
      }, 0),
      distance: routes.reduce((sum, r) => sum + r.total_distance_km, 0),
      versoDurationMin: routes.reduce((sum, r) => sum + (r.total_duration_min || 0), 0),
      googleDurationMin: routes.reduce((sum, r) => sum + (r.google_eta_total_min || 0), 0),
      cost: routes.reduce((sum, r) => sum + (r.total_cost || 0), 0),
    };
  }, [routes, routeStatusMap]);

  // Visible routes for map - start with empty set, will be populated when routes load
  const [visibleRouteIds, setVisibleRouteIds] = useState<Set<string>>(new Set());

  // Shift filter: "which routes do we have this shift". run_slot is SHIFT1/SHIFT2
  // on shift-mode runs and a free-form label on manual ones, so hand-built routes
  // get their own bucket — without it they'd vanish the moment a filter is on,
  // and those are usually the ones ops is looking for.
  const [shiftFilter, setShiftFilter] = useState<string>('ALL');
  const MANUAL_BUCKET = '__manual__';
  // A generated run's scope code is SHIFT1/SHIFT2 in shift mode, S1..S5 in slot
  // mode, and COLD for the temperature-control pass. Matching only SHIFT* filed
  // every other generated route under "Manual", which reads as "a human built
  // this" — the opposite of the truth.
  const GENERATED_SCOPE = /^(SHIFT\d+|S\d+|COLD)$/;
  const bucketOf = (route: Route) =>
    route.run_slot && GENERATED_SCOPE.test(route.run_slot) ? route.run_slot : MANUAL_BUCKET;

  const shiftBuckets = useMemo(() => {
    const counts = new Map<string, number>();
    (routes ?? []).forEach((r) => counts.set(bucketOf(r), (counts.get(bucketOf(r)) ?? 0) + 1));
    // Shifts first in name order, Manual last.
    return [...counts.entries()]
      .sort(([a], [b]) =>
        a === MANUAL_BUCKET ? 1 : b === MANUAL_BUCKET ? -1 : a.localeCompare(b)
      )
      .map(([code, count]) => ({
        code,
        count,
        label: code === MANUAL_BUCKET ? 'Manual' : code === 'COLD' ? 'Temp control' : code,
      }));
  }, [routes]);

  const filteredRoutes = useMemo(
    () => (shiftFilter === 'ALL' ? routes ?? [] : (routes ?? []).filter((r) => bucketOf(r) === shiftFilter)),
    [routes, shiftFilter]
  );

  // Card colour falls back to position, so index off the FULL list — otherwise
  // filtering repaints every route.
  const routeIndexById = useMemo(
    () => new Map((routes ?? []).map((r, i) => [r._id, i])),
    [routes]
  );

  // A filter that outlives its routes (date change, refresh) would show an empty
  // list with no obvious cause.
  useEffect(() => {
    if (shiftFilter !== 'ALL' && !shiftBuckets.some((b) => b.code === shiftFilter)) {
      setShiftFilter('ALL');
    }
  }, [shiftBuckets, shiftFilter]);
  // Track the previous route IDs to detect when routes actually change
  const [prevRouteIds, setPrevRouteIds] = useState<string[]>([]);

  // Initialize visible routes when routes change - show all routes by default only on route list change
  useEffect(() => {
    if (routes && routes.length > 0) {
      const currentRouteIds = routes.map(r => r._id);
      const currentRouteIdsSet = new Set(currentRouteIds);

      // Check if the route list has actually changed (different IDs, not just user toggling visibility)
      const routeIdsChanged = currentRouteIds.length !== prevRouteIds.length ||
        currentRouteIds.some(id => !prevRouteIds.includes(id));

      // Only reset visibility when routes actually change (date change, new routes, etc.)
      if (routeIdsChanged) {
        setVisibleRouteIds(currentRouteIdsSet);
        localStorage.setItem('visibleRouteIds', JSON.stringify(currentRouteIds));
        setPrevRouteIds(currentRouteIds);
      }
    }
  }, [routes, prevRouteIds]);

  // Get selected route for detail panel
  const selectedRoute = useMemo(() => {
    if (!routes || !selectedRouteId) return null;
    return routes.find(r => r._id === selectedRouteId) || null;
  }, [routes, selectedRouteId]);

  // Get stops for selected route
  const selectedRouteStops = useMemo(() => {
    if (!selectedRouteId) return [];
    return stopsMap.get(selectedRouteId) || [];
  }, [selectedRouteId, stopsMap]);


  // Get available drivers for a specific route (exclude drivers assigned to OTHER active routes)
  const getAvailableDriversForRoute = (routeId: string) => {
    if (!routes || !drivers) return drivers || [];

    // Get driver UIDs assigned to other active routes (not this one, and not completed)
    const otherAssignedDriverUids = new Set(
      routes
        .filter(r => r.driver_uid && r._id !== routeId && r.status !== 'completed')
        .map(r => r.driver_uid)
    );

    return drivers.filter(d => !otherAssignedDriverUids.has(d.driver_uid));
  };

  // Get available drivers for the selected route (for RouteDetailPanel)
  const availableDrivers = useMemo(() => {
    if (!selectedRouteId) return drivers || [];
    return getAvailableDriversForRoute(selectedRouteId);
  }, [routes, drivers, selectedRouteId]);


  // Handle remove stop from route
  const handleRemoveStop = async (routeId: string, stopId: string) => {
    try {
      await removeStopFromRoute(routeId, stopId);
      setSuccessMessage('Stop removed from route');

      // Refresh route status to update progress
      const result = await syncRouteStatus(routeId);
      setRouteStatusMap(prev => new Map(prev).set(routeId, result));

      await refetchRoutes();
      // Refresh stops for this route
      const routeData = await getRoute(routeId);
      if (routeData.stops) {
        setStopsMap(prev => new Map(prev).set(routeId, routeData.stops));
      }
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Failed to remove stop:', error);
      // The 409 carries the reason (driver still holds the task in Core) —
      // show it, not axios's "Request failed with status code 409".
      const serverMessage = (error as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setErrorMessage(serverMessage || (error instanceof Error ? error.message : 'Failed to remove stop'));
      setTimeout(() => setErrorMessage(null), 5000);
    }
  };

  // Handle mark task as done
  const handleMarkTaskAsDone = async (taskUid: string) => {
    try {
      await markTaskAsDone(taskUid);
      setSuccessMessage('Task marked as done successfully');

      // Refresh route status to get updated task statuses
      if (selectedRouteId) {
        // Small delay to allow Core API to propagate status change to MySQL
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Retry sync up to 3 times if task status not updated
        let result = await syncRouteStatus(selectedRouteId);
        let retries = 0;
        while (retries < 3) {
          const taskStop = result.stops.find(s => s.task_uid === taskUid);
          if (taskStop?.task_status === 'done') break;
          await new Promise(resolve => setTimeout(resolve, 500));
          result = await syncRouteStatus(selectedRouteId);
          retries++;
        }
        
        setRouteStatusMap(prev => new Map(prev).set(selectedRouteId, result));
        
        // Refresh stops for the selected route to update UI immediately
        const routeData = await getRoute(selectedRouteId);
        if (routeData.stops) {
          setStopsMap(prev => new Map(prev).set(selectedRouteId, routeData.stops));
        }
      }

      // Refetch all routes to update the list
      await refetchRoutes();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Failed to mark task as done:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Failed to mark task as done');
      setTimeout(() => setErrorMessage(null), 5000);
    }
  };

  // Abort controller ref for cancelling stale task fetches
  const fetchTasksAbortRef = useRef<AbortController | null>(null);

  // Fetch unassigned tasks for the add stop modal date range
  const fetchAddStopTasks = useCallback(async (startDate: string, endDate: string) => {
    // Cancel any in-flight fetch
    fetchTasksAbortRef.current?.abort();
    const controller = new AbortController();
    fetchTasksAbortRef.current = controller;

    setIsLoadingTasks(true);
    try {
      /*
        One range query, not one call per day. Passing an array makes tasks.api
        fire N sequential requests — measured on prod for 08/01–08/06 that is
        7.4s of spinner against 1.6s for the equivalent `from`/`to` call, and it
        grows with the range the officer picks.
      */
      const response = await getTasks({ from: startDate, to: endDate });

      // If this fetch was superseded, discard the result
      if (controller.signal.aborted) return;

      // Filter out tasks that are already planned in a route (MongoDB)
      // OR already assigned to a driver in Core DB (task_list_id set)
      const unassigned = response.tasks.filter(
        (task) => !task.is_planned && !task.assigned_driver_uid
      );
      setUnassignedTasks(unassigned);
    } catch (error) {
      if (controller.signal.aborted) return;
      console.error('Failed to fetch tasks:', error);
      setErrorMessage('Failed to load unassigned tasks');
      setTimeout(() => setErrorMessage(null), 5000);
    } finally {
      if (!controller.signal.aborted) {
        setIsLoadingTasks(false);
      }
    }
  }, []);

  // Handle add stop to route - opens modal to select task
  const handleAddStop = async () => {
    if (!selectedRouteId) return;

    setShowAddStopModal(true);
    setAddStopSearch('');
    setAddStopStartDate(date);
    setAddStopEndDate(date);
    // Clear stale tasks immediately so user doesn't see old data
    setUnassignedTasks([]);

    await fetchAddStopTasks(date, date);
  };

  // Handle selecting a task to add to route
  const handleSelectTaskToAdd = async (taskUid: string) => {
    if (!selectedRouteId) return;

    setIsAddingStop(true);
    try {
      let result;
      try {
        result = await addStopToRoute(selectedRouteId, taskUid);
      } catch (error: unknown) {
        /*
          The refrigerated-van rule warns, it does not decide. The officer is
          standing in the warehouse and can see which van has the cooler in it;
          Schedules is not always up to date. Saying yes here re-sends the add
          with the acknowledgement and the server logs the override against
          this user. Any other error falls through to the catch below.
        */
        const data = (error as { response?: { data?: { code?: string; error?: string } } })?.response?.data;
        if (data?.code !== 'COLD_DRIVER_MISMATCH' || !confirm(`${data.error}\n\nAdd this stop anyway?`)) throw error;
        result = await addStopToRoute(selectedRouteId, taskUid, true);
      }
      setSuccessMessage(result.message);
      setShowAddStopModal(false);
      await refetchRoutes();
      // Refresh stops for this route
      const routeData = await getRoute(selectedRouteId);
      if (routeData.stops) {
        setStopsMap(prev => new Map(prev).set(selectedRouteId, routeData.stops));
      }
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error: unknown) {
      console.error('Failed to add stop:', error);
      // Extract server error message from axios error response
      const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
      const serverMessage = axiosError?.response?.data?.error;
      const errorMsg = serverMessage || (error instanceof Error ? error.message : 'Failed to add stop to route');
      setErrorMessage(errorMsg);
      setTimeout(() => setErrorMessage(null), 5000);
    } finally {
      setIsAddingStop(false);
    }
  };

  // Filter unassigned tasks by search
  const filteredUnassignedTasks = useMemo(() => {
    if (!addStopSearch) return unassignedTasks;
    /*
      Normalised both sides. Core keeps merchant names with doubled internal
      spaces and HTML collapses them, so searching this picker for the name the
      Tasks page displays used to return nothing — which is how a 188-order
      Zone C pickup read as "not in the list" while sitting in the response the
      modal had already loaded. See utils/search.ts.
    */
    return unassignedTasks.filter(task =>
      matchesSearch(addStopSearch, task.merchant_name, task.uid, task.platform_code)
    );
  }, [unassignedTasks, addStopSearch]);

  // Handle route card click - open detail panel
  const handleRouteClick = (routeId: string) => {
    setSelectedRouteId(routeId);
    setShowDetailPanel(true);
  };

  // Handle refresh statuses - sync task statuses from Core DB
  const handleRefreshStatuses = async () => {
    if (!routes || routes.length === 0) return;

    setIsRefreshing(true);
    try {
      // Sync status for all routes in parallel
      const syncPromises = routes.map(route => syncRouteStatus(route._id));
      const results = await Promise.all(syncPromises);

      // Update route status map
      const newStatusMap = new Map<string, SyncStatusResult>();
      results.forEach((result, index) => {
        newStatusMap.set(routes[index]._id, result);
      });
      setRouteStatusMap(newStatusMap);

      // Refetch routes to get updated statuses
      await refetchRoutes();

      // Show success message
      const changedCount = results.filter(r => r.statusChanged).length;
      if (changedCount > 0) {
        setSuccessMessage(`Refreshed statuses for ${routes.length} routes. ${changedCount} route(s) status changed.`);
      } else {
        setSuccessMessage(`Refreshed statuses for ${routes.length} routes. No status changes.`);
      }
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Failed to refresh statuses:', error);
      setErrorMessage('Failed to refresh route statuses');
      setTimeout(() => setErrorMessage(null), 5000);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Auto-select first route when routes load
  useEffect(() => {
    if (routes && routes.length > 0 && !selectedRouteId) {
      setSelectedRouteId(routes[0]._id);
    }
  }, [routes, selectedRouteId]);

  return (
    <div className="h-[calc(100vh-3.5rem)] lg:h-screen flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-white flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Routes</h1>
          <div className="flex items-center gap-2">
            <p className="text-gray-500 text-sm">View dispatched routes for the day</p>
            {/* Polling indicator */}
            {(isPolling || isFetchingRoutes) && (
              <span className="inline-flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                <RefreshCw className="h-3 w-3 animate-spin" />
                Refreshing...
              </span>
            )}
            {lastPolledAt && !isPolling && !isFetchingRoutes && (
              <span className="text-xs text-gray-500">
                Last updated: {lastPolledAt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Baghdad' })}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            onClick={handleRefreshStatuses}
            disabled={isRefreshing || !routes || routes.length === 0}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
            title="Re-pull task statuses from Core (merchant cancellations, completions) and refresh the routes"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Syncing...' : 'Refresh & Sync'}
          </Button>
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-gray-400" />
            <Input
              type="date"
              value={date}
              onChange={(e) => {
                setDate(e.target.value);
                setSelectedRouteId(null);
              }}
              className="w-40"
            />
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="px-4 py-3 bg-green-50 border-b border-green-200 flex items-center gap-2">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <span className="text-sm text-green-800">{successMessage}</span>
        </div>
      )}

      {/* Error Message */}
      {errorMessage && (
        <div className="px-4 py-3 bg-red-50 border-b border-red-200 flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <span className="text-sm text-red-800">{errorMessage}</span>
        </div>
      )}

      {/* Loading Stops Indicator */}
      {isLoadingStops && (
        <div className="px-4 py-2 bg-blue-50 border-b border-blue-200 flex items-center gap-2">
          <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />
          <span className="text-sm text-blue-800">Loading route details...</span>
        </div>
      )}

      {/* Summary Stats - Total for all routes */}
      {routes && routes.length > 0 && (
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-6 flex-wrap">
          <div className="flex items-center gap-2">
            <Truck className="h-4 w-4 text-blue-500" />
            <span className="text-sm"><strong>{summary.routes}</strong> routes</span>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-green-500" />
            <span className="text-sm"><strong>{summary.stops}</strong> stops</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm"><strong>{routes?.reduce((sum, r) => sum + (stopsMap.get(r._id)?.reduce((s, stop) => s + (stop.parcel_count || 0), 0) || 0), 0) || 0}</strong> orders</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm"><strong>{summary.distance.toFixed(1)}</strong> km</span>
          </div>
          {/* Time breakdown (in hours) */}
          <div className="flex items-center gap-2 text-green-600" title="Total time">
            <span className="text-sm">⏱️ <strong>{(summary.versoDurationMin / 60).toFixed(1)}h</strong> total</span>
          </div>
          <div className="flex items-center gap-2 text-green-600" title="Total cost">
            <span className="text-sm"><strong>${(summary.cost || 0).toFixed(2)}</strong> cost</span>
          </div>
        </div>
      )}

      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Routes List */}
        <div className="w-80 border-r border-gray-200 bg-white flex flex-col overflow-hidden">
          {/* Routes Header */}
          <div className="p-3 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-medium text-gray-900">
              Routes
              {routes && (
                <span className="text-gray-500 font-normal ml-1">
                  {shiftFilter === 'ALL'
                    ? `(${routes.length})`
                    : `(${filteredRoutes.length} of ${routes.length})`}
                </span>
              )}
            </h3>
            {/* Show All / Hide All Icons */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => {
                  // Follows the filter — "show all" means all of what's listed.
                  const allIds = new Set(filteredRoutes.map(r => r._id));
                  setVisibleRouteIds(allIds);
                  localStorage.setItem('visibleRouteIds', JSON.stringify(Array.from(allIds)));
                }}
                className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                title="Show all routes on map"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </button>
              <button
                onClick={() => {
                  setVisibleRouteIds(new Set());
                  localStorage.setItem('visibleRouteIds', JSON.stringify([]));
                }}
                className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                title="Hide all routes from map"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
              </button>
            </div>
          </div>

          {/* Shift chips — only worth showing when there's more than one bucket */}
          {shiftBuckets.length > 1 && (
            <div className="flex flex-wrap gap-1 border-b border-gray-200 px-3 py-2">
              <button
                onClick={() => setShiftFilter('ALL')}
                className={`rounded-full px-2 py-0.5 text-xs font-medium transition-colors ${
                  shiftFilter === 'ALL'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                All {routes?.length ?? 0}
              </button>
              {shiftBuckets.map((bucket) => (
                <button
                  key={bucket.code}
                  onClick={() => setShiftFilter(bucket.code)}
                  className={`rounded-full px-2 py-0.5 text-xs font-medium transition-colors ${
                    shiftFilter === bucket.code
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  title={
                    bucket.code === MANUAL_BUCKET
                      ? 'Routes built by hand — not generated for a shift'
                      : `Routes generated for ${bucket.code}`
                  }
                >
                  {bucket.label} {bucket.count}
                </button>
              ))}
            </div>
          )}

          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {isLoadingRoutes ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : filteredRoutes.length > 0 ? (
              filteredRoutes.map((route) => {
                const index = routeIndexById.get(route._id) ?? 0;
                const routeOp = routeOperations.get(route._id);
                const isOperating = routeOp?.isLoading;
                const hasFailures = routeOp?.coreApiStatus && routeOp.coreApiStatus.failedTasks > 0;
                const failedTasks = routeOp?.operation === 'assign'
                  ? routeOp?.coreApiStatus?.failedAssignments
                  : routeOp?.coreApiStatus?.failedUnassignments;

                // Active (non-cancelled) stop count for the card, matching the detail
                // panel. Subtract the synced cancelled count from the stored stop_count
                // rather than re-counting synced stops: this preserves the card's exact
                // baseline (depot rows never enter progress.cancelled, which counts only
                // cancelled task stops). Falls back to stop_count until status is synced.
                const syncedStatus = routeStatusMap.get(route._id);
                const activeStopCount = syncedStatus
                  ? Math.max(0, route.stop_count - (syncedStatus.progress.cancelled ?? 0))
                  : undefined;

                return (
                  <div key={route._id} className="relative">
                    {/* Loading overlay with progress bar */}
                    {isOperating && (
                      <div className="absolute inset-0 bg-white/90 z-10 flex items-center justify-center rounded-lg">
                        <div className="flex flex-col items-center gap-2 w-full px-4">
                          <Loader2 className="h-5 w-5 animate-spin text-purple-600" />
                          <span className="text-sm font-medium text-gray-700">
                            {routeOp?.operation === 'assign' || routeOp?.operation === 'change'
                              ? 'Assigning tasks...'
                              : 'Unassigning tasks...'}
                          </span>
                          {/* Progress bar */}
                          {routeOp?.progress && (
                            <div className="w-full max-w-[200px]">
                              <div className="flex justify-between text-xs text-gray-500 mb-1">
                                <span>{routeOp.progress.current} / {routeOp.progress.total}</span>
                                <span>{Math.round((routeOp.progress.current / routeOp.progress.total) * 100)}%</span>
                              </div>
                              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-purple-600 transition-all duration-150 ease-out"
                                  style={{ width: `${(routeOp.progress.current / routeOp.progress.total) * 100}%` }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <RouteCard
                      route={route}
                      routeIndex={index}
                      isSelected={route._id === selectedRouteId}
                      isVisible={visibleRouteIds.has(route._id)}
                      onClick={() => handleRouteClick(route._id)}
                      onToggleVisibility={(routeId) => {
                        setVisibleRouteIds(prev => {
                          const newSet = new Set(prev);
                          if (newSet.has(routeId)) {
                            newSet.delete(routeId);
                          } else {
                            newSet.add(routeId);
                          }
                          localStorage.setItem('visibleRouteIds', JSON.stringify(Array.from(newSet)));
                          return newSet;
                        });
                      }}
                      drivers={getAvailableDriversForRoute(route._id)}
                      onAssignDriver={handleAssignDriver}
                      onRemoveDriver={handleRemoveDriver}
                      onDeleteRoute={handleDeleteRoute}
                      isDeleting={isDeletingRoute === route._id}
                      progress={routeStatusMap.get(route._id)?.progress}
                      activeStopCount={activeStopCount}
                    />

                    {/* Failed tasks panel */}
                    {hasFailures && failedTasks && failedTasks.length > 0 && (
                      <div className="mt-1 p-2 bg-red-50 border border-red-200 rounded-lg text-xs">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-red-700 font-medium flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            {failedTasks.length} task(s) failed
                          </span>
                          {can('routes.edit') && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRetryAllTasks(route._id)}
                              disabled={isOperating}
                              className="h-6 text-xs px-2"
                            >
                              Retry All
                            </Button>
                          )}
                        </div>
                        <div className="space-y-1 max-h-24 overflow-y-auto">
                          {failedTasks.map((task) => (
                            <div key={task.taskUid} className="flex items-center justify-between bg-white p-1.5 rounded border border-red-100">
                              <span className="text-gray-600 truncate flex-1 mr-2" title={task.taskUid}>
                                {task.taskUid.slice(0, 12)}...
                              </span>
                              {can('routes.edit') && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleRetryTask(route._id, task.taskUid, routeOp?.operation as 'assign' | 'unassign', task.priority)}
                                  disabled={isOperating}
                                  className="h-5 text-xs px-1.5 text-blue-600 hover:text-blue-700"
                                >
                                  Retry
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Success indicator */}
                    {routeOp?.coreApiStatus?.success && !isOperating && (
                      <div className="mt-1 p-2 bg-green-50 border border-green-200 rounded-lg text-xs flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-green-600" />
                        <span className="text-green-700">
                          {routeOp.coreApiStatus.assignedTasks || routeOp.coreApiStatus.unassignedTasks || 0} tasks processed successfully
                        </span>
                      </div>
                    )}
                  </div>
                );
              })
            ) : routes && routes.length > 0 ? (
              // Routes exist, the filter just hides them — say which filter, or
              // this reads as "the date has no routes".
              <div className="py-8 text-center text-sm text-gray-500">
                <p>No routes in {shiftFilter === MANUAL_BUCKET ? 'Manual' : shiftFilter}.</p>
                <button
                  onClick={() => setShiftFilter('ALL')}
                  className="mt-1 text-blue-600 hover:underline"
                >
                  Show all {routes.length}
                </button>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 text-sm">
                No routes found for this date
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Map & Timeline */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Map */}
          <div className="flex-1 relative">
            <RouteMap
              routes={routes || []}
              stops={stopsMap}
              selectedRouteId={selectedRouteId || undefined}
              selectedStopId={selectedStopId || undefined}
              visibleRouteIds={visibleRouteIds}
              onStopSelect={(stop: RouteStop) => setSelectedStopId(stop._id)}
              className="absolute inset-0"
              centerOnLocation={mapCenterLocation}
              routeStatusMap={routeStatusMap}
            />
          </div>

        </div>
      </div>

      {/* Route Detail Panel - Slide-in from right */}
      {selectedRoute && (
        <RouteDetailPanel
          route={selectedRoute}
          stops={selectedRouteStops}
          drivers={availableDrivers}
          isOpen={showDetailPanel}
          onClose={() => setShowDetailPanel(false)}
          onAssignDriver={handleAssignDriver}
          onRemoveDriver={handleRemoveDriver}
          onDeleteRoute={handleDeleteRoute}
          isDeleting={isDeletingRoute === selectedRouteId}
          onRemoveStop={handleRemoveStop}
          onAddStop={handleAddStop}
          onRefresh={async () => {
            await refetchRoutes();
            if (selectedRouteId) {
              const routeData = await getRoute(selectedRouteId);
              if (routeData.stops) {
                setStopsMap(prev => new Map(prev).set(selectedRouteId, routeData.stops));
              }
            }
          }}
          onStopClick={(stop) => {
            // Zoom to stop on map
            if (stop.lat && stop.lng) {
              setMapCenterLocation({ lat: stop.lat, lng: stop.lng });
            }
          }}
          stopsWithStatus={selectedRouteId ? routeStatusMap.get(selectedRouteId)?.stops : undefined}
          onMarkTaskAsDone={handleMarkTaskAsDone}
        />
      )}

      {/* Add Stop Modal */}
      {showAddStopModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[80vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Add Stop to Route</h3>
              <button
                onClick={() => setShowAddStopModal(false)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Date Range Filter */}
            <div className="px-4 pt-3 pb-2 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">From</label>
                  <input
                    type="date"
                    value={addStopStartDate}
                    onChange={(e) => {
                      const newStart = e.target.value;
                      setAddStopStartDate(newStart);
                      // If start > end, adjust end
                      const adjusted = newStart > addStopEndDate ? newStart : addStopEndDate;
                      setAddStopEndDate(adjusted);
                      fetchAddStopTasks(newStart, adjusted);
                    }}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">To</label>
                  <input
                    type="date"
                    value={addStopEndDate}
                    min={addStopStartDate}
                    onChange={(e) => {
                      const newEnd = e.target.value;
                      setAddStopEndDate(newEnd);
                      fetchAddStopTasks(addStopStartDate, newEnd);
                    }}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
              </div>
            </div>

            {/* Search */}
            <div className="p-4 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by merchant name or UID..."
                  value={addStopSearch}
                  onChange={(e) => setAddStopSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
            </div>

            {/* Task List */}
            <div className="flex-1 overflow-y-auto p-4">
              {isLoadingTasks ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                </div>
              ) : filteredUnassignedTasks.length === 0 ? (
                <div className="text-center py-8 text-gray-500 text-sm">
                  {unassignedTasks.length === 0 ? 'No unassigned tasks available' : 'No tasks match your search'}
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredUnassignedTasks.map((task) => (
                    <button
                      key={task.uid}
                      onClick={() => handleSelectTaskToAdd(task.uid)}
                      disabled={isAddingStop}
                      className="w-full p-3 text-left border border-gray-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-colors disabled:opacity-50"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-gray-900">
                            {task.merchant_name || 'Unknown Merchant'}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5 font-mono">
                            {task.uid}
                          </div>
                          <div className="text-xs text-gray-500 mt-1 flex items-center gap-1.5">
                            <span>
                              {task.number_of_orders} orders • {task.item_type === 'rto' ? 'RTO' : task.item_type === 'cash' ? 'Cash' : 'Pickup'}
                            </span>
                            <CoolingBadge count={task.cooling_count} />
                          </div>
                        </div>
                        <Plus className="h-4 w-4 text-purple-600" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <p className="text-xs text-gray-500 text-center">
                Select a task to add to the route. The route will be automatically re-optimized.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
